import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { postErrorReport } from "../../util/postErrorReport";

const MAX_CONSECUTIVE_RECOVERIES = 5;
const CONSECUTIVE_WINDOW_MS = 1000;

interface Props {
  children: ReactNode;
  currentUserId: number;
}

interface State {
  hasError: boolean;
  fatalError: Error | null;
}

class RemountOnErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    fatalError: null,
  };

  private consecutiveRecoveries = 0;
  private lastRecoveryAt = 0;
  private hasReported = false;

  public static getDerivedStateFromError(_: Error): Partial<State> {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = Date.now();
    this.consecutiveRecoveries =
      now - this.lastRecoveryAt < CONSECUTIVE_WINDOW_MS ? this.consecutiveRecoveries + 1 : 1;
    this.lastRecoveryAt = now;

    if (this.consecutiveRecoveries > MAX_CONSECUTIVE_RECOVERIES) {
      this.setState({ fatalError: error });
      return;
    }

    if (!this.hasReported) {
      this.hasReported = true;
      postErrorReport({
        environment: "React",
        trace: {
          name: error.name,
          message: error.message,
          stack: errorInfo,
        },
        context_infos: {
          NODE_ENV: process.env.REACT_APP_DEBUG ? "test" : process.env.NODE_ENV,
          errorPath: window.location.pathname,
          userId: this.props.currentUserId,
          recovered: true,
        },
      });
    }

    this.setState({ hasError: false });
  }

  public render() {
    if (this.state.fatalError) {
      throw this.state.fatalError;
    }
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export default RemountOnErrorBoundary;
